import { IPaginationResult } from '@models/Equipment.model';
import ManufacturerDocuments, {
  IManufacturerDocuments,
  IManufacturerDocumentsApiModel,
} from '@models/ManufacturerDocuments.model';
import ManufacturerResources, {
  IManufacturerResources,
  IManufacturerResourcesApiModel,
} from '@models/ManufacturerResources.model';
import Product, { IProduct, IProductApiModel } from '@models/Product.model';
import { removeDuplicatesArray } from '@utils';
import { sortStr } from '@utils/sorting/non-ascii';
import ManufacturerAddress, {
  IManufacturerAddress,
  IManufacturerAddressApiModel,
} from './ManufacturerAddress.model';
import ManufacturerContact, {
  IManufacturerContact,
  IManufacturerContactApiModel,
} from './ManufacturerContact.model';
import Tag, { ITag, ITagApiModel, ITagRaw } from './Tag.model';

export interface IManufacturerView {
  id: string;
  name: string | null | undefined;
}
export interface IManufacturer {
  id: string;
  name: string;
  status: string | null;
  manufacturerAddresses: IManufacturerAddress[];
  manufacturerContacts: IManufacturerContact[];
  resourceManufacturers: IManufacturerResources[];
  documentManufacturers: IManufacturerDocuments[];
  createdAt: Date;
  updatedAt: Date;
  products: IProduct[];
  imageUrl?: string | null;
  // admFields
  productsCount: number;
  contactsCount: number;
  resourcesCount: number;
  documentsCount: number;
  productsContactsCount: number;
  productsResourcesCount: number;
  productsDocumentsCount: number;
  manufacturerContactsCount: number;
  manufacturerResourcesCount: number;
  manufacturerDocumentsCount: number;
  equipmentsCount: number;
  tags?: ITag[];
}

export interface IManufacturerApiModel {
  id: string;
  name: string | null;
  status: string | null;
  manufacturerAddresses?: IManufacturerAddressApiModel[];
  manufacturerContacts?: IManufacturerContactApiModel[];
  resourceManufacturers?: IManufacturerResourcesApiModel[];
  documentManufacturers?: IManufacturerDocumentsApiModel[];
  products?: IProductApiModel[];
  createdAt: string;
  updatedAt: string;
  imageUrl?: string | null;
  productsCount?: number;
  resourcesCount?: number;
  contactsCount?: number;
  documentsCount?: number;
  productsContactsCount?: number;
  productsResourcesCount?: number;
  productsDocumentsCount?: number;
  equipmentsCount?: number;
  tags?: ITagApiModel[];
}

interface ITagAgg {
  f1: string;
  f2: string;
  f3: string;
  f4: string;
  f5: string;
}

export interface IManufacturerCreateUpdate {
  id: string;
  name: string;
  image: string;
  equipmentsCount: number;
  tags: Array<ITagRaw>;
}

export interface IManufacturerCreateUpdateApiModel {
  id: string;
  name: string;
  image: string;
  equipmentsCount: string;
  tags: Array<ITagAgg>;
}

export interface IManufacturerApiModel2 {
  id: string | null;
  name: string | null;
  imageUrl: string | null;
}

export default class Manufacturer implements IManufacturer {
  public readonly id: string;

  public readonly name: string;

  public readonly status: string | null;

  public readonly manufacturerAddresses: IManufacturerAddress[];

  public readonly manufacturerContacts: IManufacturerContact[];

  public readonly resourceManufacturers: IManufacturerResources[];

  public readonly documentManufacturers: IManufacturerDocuments[];

  public readonly createdAt: Date;

  public readonly updatedAt: Date;

  public readonly imageUrl?: string | null;

  public readonly products: IProduct[];

  public readonly productsCount: number;

  public readonly productsContactsCount: number;

  public readonly productsResourcesCount: number;

  public readonly productsDocumentsCount: number;

  public readonly manufacturerContactsCount: number;

  public readonly manufacturerResourcesCount: number;

  public readonly manufacturerDocumentsCount: number;

  public readonly resourcesCount: number;

  public readonly contactsCount: number;

  public readonly documentsCount: number;

  public readonly equipmentsCount: number;

  public readonly tags?: ITag[];

  constructor(
    id: string,
    name: string,
    status: string | null,
    createdAt: Date,
    updatedAt: Date,
    manufacturerAddresses: IManufacturerAddress[],
    manufacturerContacts: IManufacturerContact[],
    resourceManufacturers: IManufacturerResources[],
    documentManufacturers: IManufacturerDocuments[],
    products: IProduct[],
    productsCount: number,
    productsContactsCount: number,
    productsResourcesCount: number,
    productsDocumentsCount: number,
    manufacturerContactsCount: number,
    manufacturerResourcesCount: number,
    manufacturerDocumentsCount: number,
    resourcesCount: number,
    contactsCount: number,
    documentsCount: number,
    equipmentsCount: number,
    imageUrl?: string | null,
    tags?: ITag[],
  ) {
    this.id = id;
    this.name = name;
    this.status = status;
    this.productsContactsCount = productsContactsCount;
    this.productsResourcesCount = productsResourcesCount;
    this.productsDocumentsCount = productsDocumentsCount;
    this.manufacturerContactsCount = manufacturerContactsCount;
    this.manufacturerResourcesCount = manufacturerResourcesCount;
    this.manufacturerDocumentsCount = manufacturerDocumentsCount;
    this.manufacturerAddresses = manufacturerAddresses;
    this.manufacturerContacts = manufacturerContacts;
    this.resourceManufacturers = resourceManufacturers;
    this.documentManufacturers = documentManufacturers;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
    this.imageUrl = imageUrl;
    this.products = products;
    this.productsCount = productsCount;
    this.resourcesCount = resourcesCount;
    this.contactsCount = contactsCount;
    this.documentsCount = documentsCount;
    this.equipmentsCount = equipmentsCount;
    this.tags = tags;
  }

  static fromApiModel(apiModel: IManufacturerApiModel) {
    const { id, imageUrl, productsCount, status } = apiModel;

    const createdAt = new Date(Date.parse(apiModel.createdAt));
    const updatedAt = new Date(Date.parse(apiModel.updatedAt));

    const manufacturerAddresses = apiModel.manufacturerAddresses
      ? ManufacturerAddress.arrayFromApiModel(apiModel.manufacturerAddresses)
      : [];
    const manufacturerContacts = apiModel.manufacturerContacts
      ? ManufacturerContact.arrayFromApiModel(apiModel.manufacturerContacts)
      : [];

    const resourceManufacturers = apiModel.resourceManufacturers
      ? ManufacturerResources.arrayFromApiModel(apiModel.resourceManufacturers)
      : [];

    const documentManufacturers = apiModel.documentManufacturers
      ? ManufacturerDocuments.arrayFromApiModel(apiModel.documentManufacturers)
      : [];

    const products = apiModel.products
      ? Product.arrayFromApiModel(apiModel.products)
      : [];

    const tags = apiModel.tags ? Tag.arrayFromApiModel(apiModel.tags) : [];

    //     this.isTemp = isTemp;
    //     this.tempStudioName = tempStudioName;

    const productsContactsCount = apiModel.productsContactsCount
      ? apiModel.productsContactsCount
      : 0;
    const productsResourcesCount = apiModel.productsResourcesCount
      ? apiModel.productsResourcesCount
      : 0;

    const productsDocumentsCount = apiModel.productsDocumentsCount
      ? apiModel.productsDocumentsCount
      : 0;
    const manufacturerContactsCount = apiModel.contactsCount || 0;
    const manufacturerResourcesCount = apiModel.resourcesCount || 0;
    const manufacturerDocumentsCount = apiModel.documentsCount || 0;
    const equipmentsCount = apiModel.equipmentsCount || 0;
    const resourcesCount = productsResourcesCount + manufacturerResourcesCount;
    const contactsCount = manufacturerContactsCount + productsContactsCount;
    const documentsCount = manufacturerDocumentsCount + productsDocumentsCount;

    const manufacturerName = apiModel.name ?? '';
    return new Manufacturer(
      id,
      manufacturerName,
      status,
      createdAt,
      updatedAt,
      manufacturerAddresses,
      manufacturerContacts,
      resourceManufacturers,
      documentManufacturers,
      products,
      productsCount ?? 0,
      productsContactsCount,
      productsResourcesCount,
      productsDocumentsCount,
      manufacturerContactsCount,
      manufacturerResourcesCount,
      manufacturerDocumentsCount,
      resourcesCount,
      contactsCount,
      documentsCount,
      equipmentsCount,
      imageUrl,
      tags,
    );
  }

  static arrayFromApiModel(arrayApiModel: IManufacturerApiModel[]) {
    const array = arrayApiModel.map(apiModel =>
      Manufacturer.fromApiModel(apiModel),
    );
    return array;
  }

  static arrayPaginatedFromApiModel(
    arrayApiModel: IPaginationResult<IManufacturerApiModel>,
  ) {
    const array = arrayApiModel.results.map(apiModel =>
      Manufacturer.fromApiModel(apiModel),
    );

    return {
      ...arrayApiModel,
      results: array,
    };
  }

  static createEmptyManufacturer(name?: string): IManufacturer {
    return new Manufacturer(
      '',
      name || '',
      '',
      new Date(),
      new Date(),
      [],
      [],
      [],
      [],
      [],
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
      0,
    );
  }

  static fromCreateUpdateApiModel(
    apiModel: IManufacturerCreateUpdateApiModel,
  ): IManufacturerCreateUpdate {
    const { id, image, name } = apiModel;

    const tagsFlat = apiModel.tags.flat(1);
    const tagsFlatWithoutNull = tagsFlat.filter(item => !!item && !!item.f1);
    const tagsMap: ITagRaw[] = tagsFlatWithoutNull.map(
      ({ f1, f2, f3, f4, f5 }) => ({
        id: f1,
        name: f2,
        color: f3,
        tcId: f4,
        tcName: f5,
      }),
    );
    const _tags = removeDuplicatesArray(tagsMap);
    const tags = Array.from(_tags).sort((a, b) => {
      return sortStr(a.name, b.name);
    });

    const equipmentsCount = Number(apiModel.equipmentsCount);

    return {
      id,
      image,
      name,
      tags,
      equipmentsCount,
    };
  }

  static arrayPaginatedFromCreateUpdateApiModel(
    arrayApiModel: IPaginationResult<IManufacturerCreateUpdateApiModel>,
  ): IPaginationResult<IManufacturerCreateUpdate> {
    const array = arrayApiModel.results.map(apiModel =>
      Manufacturer.fromCreateUpdateApiModel(apiModel),
    );

    return {
      ...arrayApiModel,
      results: array,
    };
  }

  static arrayFromCreateUpdateApiModel(
    arrayApiModel: IManufacturerCreateUpdateApiModel[],
  ): IManufacturerCreateUpdate[] {
    const array = arrayApiModel.map(apiModel =>
      Manufacturer.fromCreateUpdateApiModel(apiModel),
    );

    return array;
  }
}
